import './popup.scss'
import { useLogger } from '@omnia/ui/src/lib/hooks/useLogger'
import { doesPathnameMatchAnyPageRule } from '@omnia/ui/src/lib/utils/strings/doesPathnameMatchAnyPageRule'
import { useUpdateEffect } from '@vista-digital/react-tools'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { type FC, useState } from 'react'
import { useLocation } from 'react-router-dom-v7'
import { cmsDataService } from '@/common/services/cms.data.service'
import { getSelectedCinemaGroup } from '@/features/cinema-groups/utils/get-selected-cinema-group'
import { popupPersistentDomain, popupSessionDomain } from '../domain/popup-domain'
import { FilmPopupModal } from '../subcomponents/FilmPopupModal'
import { ImagePopupModal } from '../subcomponents/ImagePopupModal'
import { hasPopupBeenViewedWithinDays } from '../utils/has-popup-been-viewed-within-days'
import { isFilmPopup } from '../utils/is-film-popup'
import { isImagePopup } from '../utils/is-image-popup'

const ComponentName = 'Popup'

export const Popup: FC = observer(() => {
    const logger = useLogger(ComponentName)
    const pathname = useLocation().pathname
    const selectedGroup = getSelectedCinemaGroup()

    const [popupViewedOnCurrentPath, setPopupViewedOnCurrentPath] = useState(false)

    const displayPopup = cmsDataService.get().popups?.find(popup => {
        // If displayFrequencyInDays is not set or is 0, check if popup has been viewed during this session (session storage)
        // Else, check if the popup has been viewed within the display frequency days (local storage)
        if (!popup.displayFrequencyInDays) {
            if (popupSessionDomain.data.viewed.includes(popup.id)) {
                logger.debug('Popup has been viewed during this session, unable to display.', {
                    popup,
                })
                return false
            }
        } else if (hasPopupBeenViewedWithinDays(popup.id, popup.displayFrequencyInDays)) {
            logger.debug('Popup has been viewed within the display frequency days, unable to display.', {
                popup,
                lastViewed: popupPersistentDomain.data.viewed[popup.id],
            })
            return false
        }

        if (!doesPathnameMatchAnyPageRule(pathname, popup.pages)) {
            logger.debug('Popup page rules do not match current page', { popup, pathname })
            return false
        }

        if (!selectedGroup) {
            logger.debug('No selected cinema group (city), skipping popups.')
            return false
        }

        if (popup.cinemaGroups?.length) {
            const match = popup.cinemaGroups.some(group => group.nodeId === selectedGroup?.id)
            if (!match) {
                logger.debug('Popup cinemaGroups do not match selected cinema group', {
                    popupGroups: popup.cinemaGroups,
                    selectedGroup,
                })
                return false
            }
        }

        logger.debug(
            'Popup matches at least one page rule and has not been viewed within the display frequency, displaying.',
            {
                popup,
                pathname,
            }
        )
        return true
    })

    logger.trace('Rendering', { pathname, displayPopup })

    const onClose = () => {
        logger.info('Closing popup', { displayPopup })
        if (!displayPopup) return

        // Add popup id to both session and persistent storage in case the display frequency
        // is updated in the CMS after a user has initially viewed this popup.
        runInAction(() => {
            popupSessionDomain.data.viewed = [...popupSessionDomain.data.viewed, displayPopup.id]
            popupPersistentDomain.data.viewed[displayPopup.id] = new Date()
        })

        setPopupViewedOnCurrentPath(true)
    }

    useUpdateEffect(() => {
        // Some pages may have multiple popups that can be displayed.
        // Once the first popup has been closed, no more popups should display for current page path, until the user navigates off the page and back.
        setPopupViewedOnCurrentPath(false)
    }, [pathname])

    if (!displayPopup || popupViewedOnCurrentPath) {
        return null
    }

    switch (true) {
        case isFilmPopup(displayPopup):
            return <FilmPopupModal popup={displayPopup} onClose={onClose} />
        case isImagePopup(displayPopup):
            return <ImagePopupModal popup={displayPopup} onClose={onClose} />
        default:
            logger.warn('Unable to determine popup type', { displayPopup })
            return null
    }
})

Popup.displayName = ComponentName
